import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, TrendingUp, AlertTriangle, Archive } from 'lucide-react';

interface InventoryData {
  products_in_stock: number;
  total_products_sold: number;
  total_products_remaining: number;
  low_stock_count: number;
  out_of_stock_count: number;
  total_products: number;
}

const InventoryOverview = () => {
  const { data: inventoryData, isLoading } = useQuery({
    queryKey: ['inventory-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_analytics')
        .select('*')
        .single();

      if (error) throw error;
      return data as InventoryData;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!inventoryData) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No inventory data available
        </CardContent>
      </Card>
    );
  }

  const stats = [
    {
      title: 'Products in Stock',
      value: inventoryData.products_in_stock.toLocaleString('en-IN'),
      icon: Package,
      description: `${inventoryData.total_products_remaining} total units`,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Total Products Sold',
      value: inventoryData.total_products_sold.toLocaleString('en-IN'),
      icon: TrendingUp,
      description: 'All-time sales',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Low Stock Alert',
      value: inventoryData.low_stock_count.toLocaleString('en-IN'),
      icon: AlertTriangle,
      description: 'Products need restocking',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      alert: inventoryData.low_stock_count > 0,
    },
    {
      title: 'Out of Stock',
      value: inventoryData.out_of_stock_count.toLocaleString('en-IN'),
      icon: Archive,
      description: 'Products unavailable',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      alert: inventoryData.out_of_stock_count > 0,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat, index) => (
        <Card key={index} className={`relative overflow-hidden ${stat.alert ? 'ring-2 ring-orange-200' : ''}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {stat.title}
            </CardTitle>
            <div className={`p-2 rounded-lg ${stat.bgColor}`}>
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">{stat.description}</p>
            </div>
            {stat.alert && (
              <Badge variant="destructive" className="mt-2 text-xs">
                Attention Required
              </Badge>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default InventoryOverview;